<script>
    import {NORMAL_USER_PERMISSIONS, ADMIN_USER_PERMISSIONS, SERVICE_ID, URLS} from '@/services/Constants';
    import UserService from '@/services/UserService';
    import PhonenumberService from '@/services/PhonenumberService';
    import VoicemailboxService from '@/services/VoicemailboxService';
    import FunctionKeySettingsService from '@/services/FunctionKeySettingsService';
    import config from './WizardConfig';
    import WizardNavigation from './WizardNavigation';
    import {saveItem} from '../../services/Storage';
    import { createXmlRpcToken } from '@/services/Authentication';

    // Admin views
    import WizardAdminFirstStep from './steps/AdminFirstStep';
    import WizardAdminSecondStep from './steps/AdminSecondStep';
    import WizardAdminThirdStep from './steps/AdminThirdStep';

    // User views
    import WizardUserFirstStep from './steps/UserFirstStep';
    import WizardUserSecondStep from './steps/UserSecondStep';
    import WizardUserThirdStep from './steps/UserThirdStep';
    import AccountService from '@/services/AccountService';

    const initialFormState = {
        externalNumbers: [],
        internalNumberId: null,
        admin: {
            selectedNumber: null,
        },
        user: {
            selectedNumber: null,
            personal: {
                firstName: '',
                familyName: '',
                email: '',
                login: '',
                password: '',
                language: 'default',
                missedCallReport: true,
                faxCoverPage: false,
                faxEmailJournal: false,
            },
        },
    };

    export default {
        name: 'Wizard',
        beforeRouteEnter(to, from, next) {
            next(vm => vm.init(to.params.type));
        },
        components: {
            WizardNavigation,
            WizardAdminFirstStep,
            WizardAdminSecondStep,
            WizardAdminThirdStep,
            WizardUserFirstStep,
            WizardUserSecondStep,
            WizardUserThirdStep,
        },
        watch: {
            '$route.params.type'(type) {
                this.init(type);
            },
        },
        data() {
            return {
                name: '',
                familyName: '',
                email: '',
                type: null,
                steps: [],
                activeStep: 1,
                form: initialFormState,
                users: [],
                error: null,
            };
        },
        methods: {
            init(type) {
                if (config && type in config) {
                    this.type = type;
                    this.steps = config[type].steps;
                    if (type === 'admin') {
                        AccountService.getAccount()
                            .then(response => {
                                if (response.status === 200) {
                                    this.name = response.data.firstname;
                                    this.familyName = response.data.lastname;
                                    this.email = response.data.email;
                                    this.steps[0].component.title = 'Hallo ' + this.name + '!';
                                }
                            });
                    }
                    this.activeStep = 1;
                    this.form = {
                        ...this.form,
                        ...initialFormState,
                    };
                    this.checkAvailability(type);
                    this.getUsers();
                    this.setupNumbers();
                } else {
                    this.$router.replace('/');
                }
            },
            onNext(value) {
                switch (this.activeStepComponent.name) {
                    case 'WizardAdminSecondStep':
                        this.form.admin.selectedNumber = value;
                        UserService.getActiveUser().then(user => {
                            const phonenumber = {
                                accountId: user.data.id,
                                phoneNumberId: this.form.admin.selectedNumber.id,
                                serviceId: SERVICE_ID.StandardCallUser,
                            };
                            const userData = {
                                firstName: this.name,
                                familyName: this.familyName,
                                email: this.email,
                                login: this.form.admin.selectedNumber.phoneNumberForLogin,
                                language: 'default',
                                missedCallReport: true,
                            };
                            UserService.updateUser(user.data.id, userData)
                                .then(response => {
                                    UserService.revokeUserLogin(user.data.id, ADMIN_USER_PERMISSIONS);
                                    FunctionKeySettingsService.setButtonForMailBoxConfiguration(+user.data.id);
                                })
                                .then(response => {
                                    this.saveNumbers(user.data.id, [phonenumber]);
                                    saveItem('loginId', userData.login, false);
                                    createXmlRpcToken(this.form.admin.selectedNumber.phoneNumberForLogin);
                                    this.activeStep++;
                                });
                        });
                        break;
                    case 'WizardUserFirstStep':
                        this.form.user.personal = {
                            ...this.form.user.personal,
                            ...value,
                        };
                        this.activeStep++;
                        break;
                    case 'WizardUserSecondStep':
                        this.form.user.selectedNumber = value;
                        this.saveUser();
                        break;
                    default:
                        this.activeStep++;
                        break;
                }
            },
            onPrev() {
                this.activeStep--;
            },
            checkAvailability(type) {
                if (type === 'admin') {
                    UserService.getActiveUser().then(user => {
                        UserService.getPhonenumbersForUser(user.data.id).then(
                            numbers => {
                                for (const phonenumber of numbers.data) {
                                    PhonenumberService.getPhonenumber(
                                        phonenumber.phoneNumberId,
                                    ).then(num => {
                                        if (num.data.type === 'EXTERNAL') {
                                            this.$router.replace('/');
                                        }
                                    });
                                }
                            },
                        );
                    });
                } else {
                    const edge = type === 'first-user' ? 2 : 3;
                    UserService.getAllUsers().then(users => {
                        if (users.data.length > edge) {
                            this.$router.replace('/');
                        }
                    });
                }
            },
            getUsers() {
                UserService.getAllUsers().then(responseUser => {
                    this.users = responseUser.data;
                });
            },
            setupNumbers() {
                PhonenumberService.getAllPhonenumbers().then(
                    responsePhonenumbers => {
                        const {
                            external,
                            internalPhoneNumberId,
                        } = PhonenumberService.splitAndSortNumbers(
                            responsePhonenumbers.data,
                            null,
                        );
                        this.form.externalNumbers = external;
                        this.form.internalNumberId = internalPhoneNumberId;
                    },
                );
            },
            saveNumbers(userId, phonenumbers) {
                UserService.assignPhonenumbersForUser(userId, phonenumbers).catch(
                    error => {
                        // console.log(error.message);
                    },
                );
            },
            saveUser() {
                if (this.type === 'first-user' || this.type === 'second-user') {
                    this.form.user.personal.login = this.form.user.selectedNumber.phoneNumberForLogin;
                    UserService.createUser(this.form.user.personal).then(responseUser => {
                        UserService.getAllUsers()
                        .then(responseAllUser => {
                            this.users = responseAllUser.data;
                            const user = UserService.findUserIdByLogin(
                                responseAllUser.data,
                                this.form.user.personal.login,
                            );
                            const phonenumbers = [{
                                accountId: user.id,
                                phoneNumberId: this.form.user
                                    .selectedNumber.id,
                                serviceId: SERVICE_ID.StandardCallUser,
                            },
                            {
                                accountId: user.id,
                                phoneNumberId: this.form.internalNumberId,
                                serviceId: SERVICE_ID.StandardCallUser,
                            }, ];
                            this.saveNumbers(user.id, phonenumbers);
                            UserService.revokeUserLogin(user.id, NORMAL_USER_PERMISSIONS);
                            // maximumDuration: -1 represents 10 min of voicemail Aufzeichnungsdauer
                            VoicemailboxService.createVoicemailbox({
                                    maximumDuration: -1,
                                    name: 'Voicemailbox ' + this.form.user.selectedNumber.phonenumberExtension,
                                    noRecord: false,
                                    number: this.form.user.selectedNumber.phonenumberExtension,
                                    password: '',
                                    voicemailUsers: [{
                                        accountId: user.id,
                                        name: this.form.user.personal.familyName + ', ' + this.form.user.personal.firstName,
                                        sendMail: true,
                                        voicemailboxName: 'Voicemailbox ' + this.form.user.selectedNumber.phonenumberExtension
                                    }],
                                    voicemailGroups: []
                                }
                            );
                            FunctionKeySettingsService.setButtonForMailBoxConfiguration(+user.id);
                            FunctionKeySettingsService.setBLFforAllUsers(user, this.users);
                            this.activeStep++;
                        })
                        .catch(error => {
                            this.error = error.response.data;
                        });
                    })
                    .catch(error => {
                            this.error = error.response.data;
                        });
                }
            },
        },
        computed: {
            activeStepComponent() {
                const activeStep = this.steps.find(
                    step => step.key === this.activeStep,
                );
                if (activeStep) {
                    return activeStep.component;
                }
            },
            getDataForComponent() {
                switch (this.activeStepComponent.name) {
                    case 'WizardAdminSecondStep':
                        return this.form.externalNumbers;
                        break;
                    case 'WizardAdminThirdStep':
                        return {
                            phonenumber: this.form.admin.selectedNumber.phonenumber,
                            phonenumberforlogin: this.form.admin.selectedNumber.phoneNumberForLogin,
                            contentText: this.activeStepComponent.contentText,
                            description2: this.activeStepComponent.description2,
                        };
                        break;
                    case 'WizardUserSecondStep':
                        return this.form.externalNumbers;
                        break;
                    case 'WizardUserThirdStep':
                        return {
                            isLast: this.type === 'first-user',
                            contentText: this.activeStepComponent.contentText,
                        };
                        break;
                    default:
                        return null;
                        break;
                }
            },

            getErrorMessage() {
                return;
            },
        },
    };
</script>

<template>
    <div class="container">
        <div id="wizard" class="wizard-wrapper">
            <WizardNavigation :steps="steps" :activeStep="activeStep"/>
            <div class="wizard-content">
                <component v-if="activeStepComponent" :is="activeStepComponent.name" :title="activeStepComponent.title"
                           :description="activeStepComponent.description" :image="activeStepComponent.image"
                           :customData="getDataForComponent" :error="error" @on_next="onNext"
                           @on_prev="onPrev">
                </component>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '../../assets/scss/variables.scss';
    @import '../../assets/scss/inputs.scss';

    .wizard-wrapper {
        height: 100%;
        .wizard-content {
            width: 100%;
            background-color: #ffffff;
            padding: 48px;
            box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);
            .wizard-content-step {
                .blocks {
                    display: -ms-grid;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    -ms-grid-columns: 1fr 1fr;
                    grid-column-gap: 0;
                    grid-row-gap: 0;
                    .block {
                        .description {
                            color: $text;
                            h1 {
                                font-weight: normal;
                                margin-top: 0;
                            }
                        }
                        .image {
                            img {
                                width: 100%;
                            }
                        }
                    }
                    .actions {
                        display: flex;
                        justify-content: flex-end;
                        align-items: baseline;
                        button {
                            display: inline-block;
                            text-align: center;
                            white-space: nowrap;
                            vertical-align: middle;
                            -webkit-user-select: none;
                            -moz-user-select: none;
                            -ms-user-select: none;
                            user-select: none;
                            border: 1px solid transparent;
                            padding: 14px 7px;
                            font-size: 16px;
                            line-height: 19px;
                            background-color: $orange;
                            color: #fff;
                        }
                    }
                }
            }
        }
    }
</style>
